import React from 'react';
import { useHistory } from 'react-router-dom';
import { 
    Container, 
    Typography, 
    Grid, 
    Card, 
    CardContent, 
    Box,
    Chip,
    Button,
    makeStyles
} from '@material-ui/core';
import {
    Camera,
    Lightbulb,
    AlertTriangle,
    CheckCircle2,
    Sparkles,
    Brain
} from 'lucide-react';
import { config } from '../../Constants';

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        paddingTop: theme.spacing(8),
        paddingBottom: theme.spacing(8),
    },
    container: {
        maxWidth: '1200px',
    },
    header: {
        textAlign: 'center',
        color: 'white',
        marginBottom: theme.spacing(6),
    },
    title: {
        fontWeight: 700,
        marginBottom: theme.spacing(2),
        fontSize: '3rem',
        [theme.breakpoints.down('sm')]: {
            fontSize: '2rem',
        },
    },
    subtitle: {
        fontSize: '1.3rem',
        opacity: 0.9,
        marginBottom: theme.spacing(4),
    },
    card: {
        height: '100%',
        borderRadius: 16,
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        transition: 'transform 0.3s ease-in-out',
        '&:hover': {
            transform: 'translateY(-4px)',
        },
    },
    cardIcon: {
        fontSize: 48,
        color: theme.palette.primary.main,
        marginBottom: theme.spacing(2),
    },
    cardTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        color: '#333',
    },
    chip: {
        margin: theme.spacing(0.5),
        fontWeight: 500,
    },
    goodChip: {
        backgroundColor: '#e8f5e8',
        color: '#2e7d32',
    },
    badChip: {
        backgroundColor: '#ffebee',
        color: '#c62828',
    },
    infoChip: {
        backgroundColor: '#e3f2fd',
        color: '#1565c0',
    },
    ctaSection: {
        textAlign: 'center',
        marginTop: theme.spacing(6),
        padding: theme.spacing(4),
        backgroundColor: 'rgba(255,255,255,0.1)',
        borderRadius: 16,
        backdropFilter: 'blur(10px)',
    },
    ctaButton: {
        padding: theme.spacing(2, 6),
        fontSize: '1.2rem',
        fontWeight: 600,
        borderRadius: 50,
        background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
        color: 'white',
        '&:hover': {
            background: 'linear-gradient(45deg, #FE6B8B 60%, #FF8E53 100%)',
            transform: 'scale(1.05)',
        },
    },
    statBox: {
        textAlign: 'center',
        color: 'white',
        margin: theme.spacing(2),
    },
    statNumber: {
        fontSize: '2.5rem',
        fontWeight: 700,
        color: '#FFD700',
    },
    statLabel: {
        fontSize: '1rem',
        opacity: 0.9,
    },
}));

const PhotoGuide = () => {
    const classes = useStyles();
    const history = useHistory();

    const handleNext = () => {
        history.push('/upload');
    };

    const guidelines = [
        {
            title: "Required Photos",
            icon: <CheckCircle2 size={48} style={{ color: '#667eea', marginBottom: '1rem' }} />,
            items: config.photoGuidelines.required,
            chipColor: "goodChip"
        },
        {
            title: "Recommended Tips",
            icon: <Lightbulb size={48} style={{ color: '#667eea', marginBottom: '1rem' }} />,
            items: config.photoGuidelines.recommended,
            chipColor: "infoChip"
        },
        {
            title: "Avoid These",
            icon: <AlertTriangle size={48} style={{ color: '#667eea', marginBottom: '1rem' }} />,
            items: config.photoGuidelines.avoid,
            chipColor: "badChip"
        }
    ];

    return (
        <Box className={classes.root}>
            <Container className={classes.container}>
                {/* Header Section */}
                <Box className={classes.header}>
                    <Typography variant="h1" className={classes.title}>
                        📸 Create Your AI Twin
                    </Typography>
                    <Typography variant="h2" className={classes.subtitle}>
                        Upload {config.maxSelfies} photos to train your personal AI model
                    </Typography>
                    
                    {/* Stats Section */}
                    <Grid container justify="center" spacing={4}>
                        <Grid item xs={4}>
                            <Box className={classes.statBox}>
                                <Typography className={classes.statNumber}>{config.maxSelfies}</Typography>
                                <Typography className={classes.statLabel}>Photos Needed</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={4}>
                            <Box className={classes.statBox}>
                                <Typography className={classes.statNumber}>15</Typography>
                                <Typography className={classes.statLabel}>Minutes to Train</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={4}>
                            <Box className={classes.statBox}>
                                <Typography className={classes.statNumber}>∞</Typography>
                                <Typography className={classes.statLabel}>Photos Generated</Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Guidelines Cards */}
                <Grid container spacing={4}>
                    {guidelines.map((guide, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <Card className={classes.card}>
                                <CardContent style={{ textAlign: 'center', padding: '2rem' }}>
                                    {guide.icon}
                                    <Typography variant="h5" className={classes.cardTitle}>
                                        {guide.title}
                                    </Typography>
                                    <Box>
                                        {guide.items.map((item, itemIndex) => (
                                            <Chip
                                                key={itemIndex}
                                                label={item}
                                                className={`${classes.chip} ${classes[guide.chipColor]}`}
                                                size="small"
                                            />
                                        ))}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Example Photos Section */}
                <Box style={{ marginTop: '4rem', marginBottom: '4rem' }}>
                    <Typography 
                        variant="h4" 
                        align="center" 
                        style={{ color: 'white', marginBottom: '2rem', fontWeight: 600 }}
                    >
                        ✅ Perfect Examples vs ❌ Avoid These
                    </Typography>
                    
                    <Grid container spacing={3}>
                        {/* Good Examples */}
                        <Grid item xs={12} md={6}>
                            <Typography 
                                variant="h6" 
                                style={{ color: '#4CAF50', marginBottom: '1rem', fontWeight: 600 }}
                            >
                                ✅ Great Photos for AI Training
                            </Typography>
                            <Grid container spacing={2}>
                                {[1, 2, 3, 4].map((num) => (
                                    <Grid item xs={6} key={num}>
                                        <Box
                                            style={{ 
                                                width: '100%',
                                                height: 200,
                                                borderRadius: 12,
                                                border: '3px solid #4CAF50',
                                                backgroundColor: '#e8f5e8',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: '#2e7d32',
                                                fontSize: '1rem',
                                                fontWeight: 600
                                            }}
                                        >
                                            <Box textAlign="center">
                                                <Camera size={40} style={{ marginBottom: 8 }} />
                                                <div>Clear Portrait</div>
                                                <div>Good Lighting</div>
                                            </Box>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>

                        {/* Bad Examples */}
                        <Grid item xs={12} md={6}>
                            <Typography 
                                variant="h6" 
                                style={{ color: '#f44336', marginBottom: '1rem', fontWeight: 600 }}
                            >
                                ❌ Photos That Won't Work Well
                            </Typography>
                            <Grid container spacing={2}>
                                {[1, 2, 3, 4].map((num) => (
                                    <Grid item xs={6} key={num}>
                                        <Box
                                            style={{ 
                                                width: '100%',
                                                height: 200,
                                                borderRadius: 12,
                                                border: '3px solid #f44336',
                                                backgroundColor: '#ffebee',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: '#c62828',
                                                fontSize: '1rem',
                                                fontWeight: 600,
                                                filter: 'grayscale(50%)'
                                            }}
                                        >
                                            <Box textAlign="center">
                                                <AlertTriangle size={40} style={{ marginBottom: 8 }} />
                                                <div>Blurry/Dark</div>
                                                <div>Filtered</div>
                                            </Box>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>

                {/* AI Magic Explanation */}
                <Card className={classes.card} style={{ marginBottom: '3rem' }}>
                    <CardContent style={{ padding: '3rem', textAlign: 'center' }}>
                        <Sparkles size={60} style={{ color: '#FFD700', marginBottom: '1rem' }} />
                        <Typography variant="h4" className={classes.cardTitle} style={{ color: '#333', marginBottom: '2rem' }}>
                            How the AI Magic Works
                        </Typography>
                        <Grid container spacing={4} alignItems="center">
                            <Grid item xs={12} md={4}>
                                <Brain size={48} style={{ color: '#667eea', marginBottom: '1rem' }} />
                                <Typography variant="h6" style={{ fontWeight: 600, marginBottom: '1rem' }}>
                                    1. AI Learns Your Face
                                </Typography>
                                <Typography variant="body1" color="textSecondary">
                                    Our advanced model analyzes your photos to understand your unique facial features, expressions, and characteristics.
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Sparkles size={48} style={{ color: '#667eea', marginBottom: '1rem' }} />
                                <Typography variant="h6" style={{ fontWeight: 600, marginBottom: '1rem' }}>
                                    2. Custom Model Created
                                </Typography>
                                <Typography variant="body1" color="textSecondary">
                                    We train a personalized AI model just for you in 15-20 minutes. This becomes your digital twin.
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Camera size={48} style={{ color: '#667eea', marginBottom: '1rem' }} />
                                <Typography variant="h6" style={{ fontWeight: 600, marginBottom: '1rem' }}>
                                    3. Generate Unlimited Photos
                                </Typography>
                                <Typography variant="body1" color="textSecondary">
                                    Use prompts to create stunning profile pictures in any style, setting, or mood you can imagine!
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* CTA Section */}
                <Box className={classes.ctaSection}>
                    <Typography variant="h4" style={{ color: 'white', marginBottom: '1rem', fontWeight: 600 }}>
                        Ready to Create Your AI Twin? 🚀
                    </Typography>
                    <Typography variant="h6" style={{ color: 'white', opacity: 0.9, marginBottom: '2rem' }}>
                        Upload your photos and start generating amazing profile pictures in minutes!
                    </Typography>
                    <Button 
                        className={classes.ctaButton}
                        size="large"
                        onClick={handleNext}
                    >
                        Start Uploading Photos →
                    </Button>
                </Box>
            </Container>
        </Box>
    );
};

export default PhotoGuide;